/**
 * AppICEMAP — SearchBar Component
 * Debounced search input for filtering reports.
 *
 * Tag: <search-bar>
 *
 * Attributes:
 *   placeholder — placeholder text (default: "Buscar reportes...")
 *   value       — controlled input value
 *
 * Dispatches:
 *   search-bar:input  → { value: string }
 *   search-bar:clear  → { }
 */

export class SearchBar extends HTMLElement {
	constructor() {
		super();
		this._debounceMs = 300;
		this._debounceTimer = null;
		this._value = "";
	}

	connectedCallback() {
		this._render();
		this._bindEvents();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	get value() {
		return this._input ? this._input.value : this._value;
	}

	set value(val) {
		this._value = val || "";
		if (this._input) {
			this._input.value = this._value;
			this._updateClearButton();
		}
	}

	focus() {
		if (this._input) this._input.focus();
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		const placeholder =
			this.getAttribute("placeholder") || "Buscar reportes...";

		this.innerHTML = `
      <div class="search-bar" role="search">
        <i class="ph ph-magnifying-glass search-bar__icon" aria-hidden="true"></i>
        <input type="search"
               class="search-bar__input"
               placeholder="${escapeAttr(placeholder)}"
               value="${escapeAttr(this._value)}"
               autocomplete="off"
               aria-label="${escapeAttr(placeholder)}">
        <button class="search-bar__clear" aria-label="Limpiar búsqueda" hidden>
          <i class="ph ph-x" aria-hidden="true"></i>
        </button>
      </div>
    `;

		this._input = this.querySelector(".search-bar__input");
		this._clearBtn = this.querySelector(".search-bar__clear");
		this._updateClearButton();
	}

	/* ── Events ────────────────────────────────────────────────────────────── */

	_bindEvents() {
		if (!this._input) return;

		this._input.addEventListener("input", () => {
			this._value = this._input.value;
			this._updateClearButton();
			this._debounce();
		});

		this._input.addEventListener("keydown", (e) => {
			if (e.key === "Escape") {
				this._clear();
			}
		});

		if (this._clearBtn) {
			this._clearBtn.addEventListener("click", () => this._clear());
		}
	}

	_debounce() {
		if (this._debounceTimer) clearTimeout(this._debounceTimer);
		this._debounceTimer = setTimeout(() => {
			this.dispatchEvent(
				new CustomEvent("search-bar:input", {
					detail: { value: this._value },
					bubbles: true,
				}),
			);
		}, this._debounceMs);
	}

	_clear() {
		this._value = "";
		if (this._input) this._input.value = "";
		this._updateClearButton();

		if (this._debounceTimer) clearTimeout(this._debounceTimer);

		this.dispatchEvent(
			new CustomEvent("search-bar:input", {
				detail: { value: "" },
				bubbles: true,
			}),
		);
		this.dispatchEvent(
			new CustomEvent("search-bar:clear", {
				bubbles: true,
			}),
		);

		if (this._input) this._input.focus();
	}

	_updateClearButton() {
		if (this._clearBtn) {
			this._clearBtn.hidden = !this._value;
		}
	}
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function escapeAttr(str) {
	return str.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

customElements.define("search-bar", SearchBar);
